import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import { OrderAPI } from "@app/services/api";
import { setEventNeedUpdate, setOrderEvents } from "@app/store/order.slice";
import { OrderEvent } from "@app/domain";
import { AsyncState } from "@app/shared/hooks/useAsync";
import { toOrderEvents } from "../adapters/toOrderEvents";

/**
 * Powers the Event History page, and Order Details' non-Ondemand
 * LastEventCard. The event list itself already arrives as part of the order
 * track response for non-Ondemand orders (`state.order.events`, populated in
 * `order.slice.ts`'s `fetchTrackOrder.fulfilled`) — no separate fetch on
 * mount.
 *
 * Also owns the legacy `EventUpdater` behavior (previously
 * `components/events-box`): whenever a status change flips
 * `state.order.isEventNeedUpdate`, re-fetch the full list from
 * `OrderAPI.getEvents` and clear the flag. That endpoint's response has no
 * `id`/`source` fields, unlike the initial track response — see
 * `toOrderEvents` for how that gap is bridged.
 *
 * `enabled` defaults to true (Event History always wants the data); Order
 * Details passes `false` for Ondemand orders, mirroring `useLastEvent`'s own
 * gating — that section renders a driver card instead and never needs this.
 */
export function useEventHistory(trackCode: string, enabled = true): AsyncState<OrderEvent[]> {
  const dispatch = useDispatch<AppDispatch>();
  const order = useSelector((state: RootState) => state.order);
  const restServerUrl = useSelector((state: RootState) => state.setting.restServerUrl);
  const language = useSelector((state: RootState) => state.setting.language);
  const accessToken = useSelector((state: RootState) => state.customer.accessToken);
  const customerId = useSelector((state: RootState) => state.customer.customerId);

  useEffect(() => {
    if (!enabled || !order.isEventNeedUpdate || !restServerUrl || !accessToken) return;
    let cancelled = false;

    new OrderAPI()
      .getEvents({ restUrl: restServerUrl, accessToken, orderId: order.orderId, customerId, language })
      .then((response) => {
        if (cancelled) return;
        dispatch(setOrderEvents(response.data));
        dispatch(setEventNeedUpdate(false));
      })
      .catch(() => {
        // Matches legacy `EventUpdater`: swallow the error, keep
        // `isEventNeedUpdate` true so it's retried on the next change.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, order.isEventNeedUpdate, order.orderId, restServerUrl, accessToken, customerId, language, dispatch]);

  if (!enabled) return { status: "success", data: [], error: undefined };

  if (order.trackOrderCode !== trackCode || order.orderFetchStatus === "loading" || order.orderFetchStatus === "idle") {
    return { status: "loading", data: undefined, error: undefined };
  }

  if (order.orderFetchStatus === "failed") {
    return { status: "error", data: undefined, error: new Error(order.error?.message ?? "Failed to load events") };
  }

  return { status: "success", data: toOrderEvents(order.events), error: undefined };
}
